import { z } from 'zod';

/**
 * The admin portal's build-time configuration, parsed ONCE.
 *
 * U14: env carries deployment infrastructure only — where the api lives, and
 * nothing else. There is no third-party credential here and there never will be
 * (N21: those live in the integrations vault, and the admin never sees one).
 * There is no brand fact here either (U16: brand name, logo and support contacts
 * come from the `brand.*` settings group at runtime).
 *
 * `import.meta.env` is read in exactly this file. A call site that read it
 * directly would be a value with no schema, discovered as `undefined` somewhere
 * downstream at runtime; here a missing variable is a boot-time throw naming it.
 *
 * Two sources, one schema: the build-time `VITE_*` value (local development),
 * overridden by `window.__APP_CONFIG__` — `/config.js`, which the production
 * image writes at container start. That is what lets one build serve staging
 * and production alike (deploy/README.md).
 */
const EnvSchema = z.object({
  /**
   * The api origin INCLUDING its global prefix, e.g. `http://localhost:3000/api`.
   * Absolute, because the SPA is served from a different origin in every
   * environment; the trailing slash is stripped so a path joins exactly once.
   */
  VITE_API_BASE_URL: z
    .url({ error: 'VITE_API_BASE_URL must be an absolute url, e.g. http://localhost:3000/api' })
    .transform((value) => value.replace(/\/+$/, '')),
});

export interface AdminEnv {
  readonly apiBaseUrl: string;
}

/**
 * Exported separately from `env` so it is testable without a Vite environment —
 * the pattern `apps/mobile`'s `resolveBootDirection` uses for the same reason.
 */
export function parseEnv(raw: Record<string, unknown>): AdminEnv {
  const result = EnvSchema.safeParse(raw);

  if (!result.success) {
    const detail = result.error.issues
      .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
      .join('; ');
    throw new Error(
      `Admin portal environment is not configured — ${detail}. Set it in apps/admin/.env.development (see .env.example).`,
    );
  }

  return { apiBaseUrl: result.data.VITE_API_BASE_URL };
}

const runtimeConfig: Record<string, unknown> =
  typeof window === 'undefined' ? {} : (window.__APP_CONFIG__ ?? {});

export const env: AdminEnv = parseEnv({
  ...(import.meta.env as unknown as Record<string, unknown>),
  ...runtimeConfig,
});
